import React, { useEffect, useState, useCallback } from "react";
import { fetchAuditLog } from "../../../services/aro/auditService";
import { AuditEntry } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import { formatDateTime } from "../../../utils/formatters";
import "./AuditPage.css";

const LIMIT = 100;

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function colorizeJson(json: string): string {
  return json
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = "aro-json-number";
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? "aro-json-key" : "aro-json-string";
        } else if (/true|false/.test(match)) {
          cls = "aro-json-bool";
        } else if (/null/.test(match)) {
          cls = "aro-json-null";
        }
        return `<span class="${cls}">${match}</span>`;
      });
}

export default function AuditPage() {
  const [entries,    setEntries]    = useState<AuditEntry[]>([]);
  const [total,      setTotal]      = useState(0);
  const [offset,     setOffset]     = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copied,     setCopied]     = useState<string | null>(null);

  const [recordId,   setRecordId]   = useState("");
  const [tableName,  setTableName]  = useState("all");

  const load = useCallback((off: number) => {
    setLoading(true);
    setError(null);
    fetchAuditLog({
      record_id:  recordId  || undefined,
      table_name: tableName,
      limit:      LIMIT,
      offset:     off,
    })
      .then((r) => { setEntries(r.audit); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [recordId, tableName]);

  useEffect(() => { setOffset(0); load(0); }, [load]);

  const copyId = (id: string) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const actionVariant = (action: AuditEntry["action"]) => {
    if (action === "INSERT" || action === "PROMOTE") return "green";
    if (action === "UPDATE") return "blue";
    if (action === "DISCARD") return "gray";
    return "red";
  };

  const from = total === 0 ? 0 : offset + 1;
  const to   = Math.min(offset + LIMIT, total);

  return (
    <div className="aro-page">
      <PageHeader title="Audit Log" subtitle={`${total} entries`} />

      <div className="aro-filter-bar">
        <input
          className="aro-input"
          placeholder="Filter by record ID…"
          value={recordId}
          onChange={(e) => setRecordId(e.target.value)}
        />
        <select className="aro-select" value={tableName} onChange={(e) => setTableName(e.target.value)}>
          <option value="all">All Tables</option>
          <option value="persons">persons</option>
          <option value="events">events</option>
          <option value="certificates">certificates</option>
          <option value="certificate_types">certificate_types</option>
          <option value="person_tracking">person_tracking</option>
          <option value="quarantine_layer">quarantine_layer</option>
        </select>
      </div>

      {error && <div className="aro-error-banner">{error}</div>}

      <div className="aro-card aro-card--no-pad">
        {loading ? (
          <LoadingSpinner />
        ) : entries.length === 0 ? (
          <EmptyState icon="📋" message="No audit entries found." />
        ) : (
          <table className="aro-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Table</th>
                <th>Record ID</th>
                <th>Action</th>
                <th>Changed By</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <React.Fragment key={e.id}>
                  <tr className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                    <td className="aro-audit__ts">{formatDateTime(e.changed_at)}</td>
                    <td>
                      <span className="aro-audit__table-name">{e.table_name}</span>
                    </td>
                    <td>
                      <span className="aro-audit__uuid">
                        {truncateId(e.record_id)}
                        <button
                          className="aro-copy-btn"
                          onClick={() => copyId(e.record_id)}
                          title="Copy full ID"
                        >
                          {copied === e.record_id ? "✓" : "⎘"}
                        </button>
                      </span>
                    </td>
                    <td>
                      <AROBadge label={e.action} variant={actionVariant(e.action)} />
                    </td>
                    <td className="aro-audit__changed-by">{e.changed_by}</td>
                    <td>
                      <button
                        className="aro-btn aro-btn--ghost aro-btn--sm"
                        onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}
                      >
                        {expandedId === e.id ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>
                  {expandedId === e.id && (
                    <tr>
                      <td colSpan={6} className="aro-audit__json-row">
                        <div className="aro-audit__json-panels">
                          {e.previous_data !== null && (
                            <div className="aro-audit__json-panel">
                              <span className="aro-audit__json-label">Previous</span>
                              <pre
                                className="aro-json"
                                dangerouslySetInnerHTML={{
                                  __html: colorizeJson(JSON.stringify(e.previous_data, null, 2)),
                                }}
                              />
                            </div>
                          )}
                          {e.new_data !== null && (
                            <div className="aro-audit__json-panel">
                              <span className="aro-audit__json-label">New</span>
                              <pre
                                className="aro-json"
                                dangerouslySetInnerHTML={{
                                  __html: colorizeJson(JSON.stringify(e.new_data, null, 2)),
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > LIMIT && (
        <div className="aro-pagination">
          <span className="aro-pagination__info">Showing {from}–{to} of {total}</span>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset === 0}
            onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o); }}
          >
            ← Previous
          </button>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset + LIMIT >= total}
            onClick={() => { const o = offset + LIMIT; setOffset(o); load(o); }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
